import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { sendPasswordResetEmail, sendSignInLinkToEmail } from "firebase/auth";
import { app, auth } from "../../../firebase";
import {
  AlertCircle,
  CheckCircle2,
  MessageCircle,
  ArrowRight,
  Loader2,
  RefreshCw,
} from "lucide-react";
import {
  WA_COMMUNITY_LINK,
  FONT,
  COURSES,
  IDENTITY,
  DISCOVERY_SOURCE,
} from "../../../Data/LandingPage/UEC2026Data";
const functions = getFunctions(app, "asia-southeast1");
const submitUecRegistration = httpsCallable(functions, "submitUecRegistration");
const EMAIL_STORAGE_KEY = "emailForUecVerification";
type AccountEmailType = "password-reset" | "verification";

const isOperationNotAllowedError = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "code" in error &&
  (error as { code?: string }).code === "auth/operation-not-allowed";

const getEmailActionLabel = (emailType: AccountEmailType) =>
  emailType === "password-reset" ? "password reset" : "verification";

export default function RegistrationForm({ id }: { id: string }) {
  const [form, setForm] = useState({
    chineseName: "",
    name: "",
    schoolName: "",
    identity: "",
    country: "Malaysia",
    countryCode: "+60",
    phoneNumber: "",
    email: "",
    discoverySource: "",
  });
  const [courses, setCourses] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [accountActivationRequired, setAccountActivationRequired] =
    useState(false);
  const [accountEmailSent, setAccountEmailSent] = useState(true);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [accountEmailType, setAccountEmailType] =
    useState<AccountEmailType>("verification");
  const [pendingSubmissionId, setPendingSubmissionId] = useState("");
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isFirstRegistration, setIsFirstRegistration] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionLockRef = useRef(false);
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = window.setTimeout(
      () => setResendCooldown((seconds) => Math.max(0, seconds - 1)),
      1000,
    );
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const sendVerificationEmail = async (email: string, submissionId: string) => {
    const verificationUrl = new URL(
      "/verify-uec-update",
      window.location.origin,
    );
    if (submissionId) {
      verificationUrl.searchParams.set("submissionId", submissionId);
    }
    await sendSignInLinkToEmail(auth, email, {
      url: verificationUrl.toString(),
      handleCodeInApp: true,
    });
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
  };

  const sendPasswordResetAccessEmail = async (email: string) => {
    window.localStorage.removeItem(EMAIL_STORAGE_KEY);
    await sendPasswordResetEmail(auth, email);
  };

  const sendAccountAccessEmail = async (
    email: string,
    submissionId: string,
    emailType: AccountEmailType,
  ) => {
    if (emailType === "password-reset") {
      await sendPasswordResetAccessEmail(email);
      return { emailType, usedFallback: false };
    }

    try {
      await sendVerificationEmail(email, submissionId);
      return { emailType, usedFallback: false };
    } catch (error) {
      if (!isOperationNotAllowedError(error)) {
        throw error;
      }

      console.warn(
        "Firebase email-link sign-in is disabled; sending password reset email instead.",
        error,
      );
      await sendPasswordResetAccessEmail(email);
      return { emailType: "password-reset" as const, usedFallback: true };
    }
  };

  const handleResendAccountEmail = async () => {
    if (!verificationEmail || isResendingEmail || resendCooldown > 0) return;

    setIsResendingEmail(true);
    setResendMessage("");
    try {
      const delivery = await sendAccountAccessEmail(
        verificationEmail,
        pendingSubmissionId,
        accountEmailType,
      );
      setAccountEmailType(delivery.emailType);
      setAccountEmailSent(true);
      setResendMessage(
        `A new ${getEmailActionLabel(
          delivery.emailType,
        )} email was sent to ${verificationEmail}.`,
      );
    } catch (emailError) {
      console.error("Failed to resend account access email:", emailError);
      setResendMessage(
        `We still couldn't send the ${
          accountEmailType === "password-reset"
            ? "password reset"
            : "verification"
        } email. Please wait and try again, or contact support.`,
      );
    } finally {
      setIsResendingEmail(false);
      setResendCooldown(30);
    }
  };

  const set = (field: string, val: string) => {
    setForm((f) => ({ ...f, [field]: val }));
    setErrors((e) => ({ ...e, [field]: "" }));
  };

  const toggleCourse = (courseValue: string) => {
    setCourses((prev) =>
      prev.includes(courseValue)
        ? prev.filter((x) => x !== courseValue)
        : [...prev, courseValue],
    );
    setErrors((e) => ({ ...e, courses: "" }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.nameEn = "请填写英文姓名";
    if (!form.schoolName.trim()) e.schoolName = "请填写学校名称";
    if (!form.identity) e.identity = "请选择身份";
    if (!form.phoneNumber.trim()) e.phoneNumber = "请填写联络号码";
    else if (!/^1(?:[02-46-9]\d{7}|1\d{8})$/.test(form.phoneNumber.trim()))
      e.phoneNumber = "请输入有效的马来西亚联络号码";
    if (!form.email.trim()) e.email = "请填写电邮";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "请输入有效电邮";
    if (courses.length === 0) e.courses = "请至少选择一个课程";
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionLockRef.current) return;
    let shouldOpenWhatsApp = false;

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    submissionLockRef.current = true;
    setIsSubmitting(true);
    setErrors((currentErrors) => ({ ...currentErrors, submit: "" }));

    const createdDate = new Date().toISOString();

    try {
      const leadData = {
        chineseName: form.chineseName.trim(),
        name: form.name.trim(),
        schoolName: form.schoolName.trim(),
        identity: form.identity,
        country: "Malaysia",
        countryCode: "+60",
        phoneNumber: form.phoneNumber.trim(),
        email: form.email.trim(),
        discoverySource: form.discoverySource,
        tuition: {
          qualification: "UEC",
          subject: courses,
        },
        status: "New",
        leadSource: "Website",
        campaign: "UEC Ultimate Revision Class 2026",
        sourceDetail: "Landing Page",
        createdDate,
        accountStatus: "not registered",
        portalProvisioningStatus: "pending",
      };

      const referralCode = new URLSearchParams(window.location.search)
        .get("ref")
        ?.trim()
        .toLowerCase();
      const provisionResult = await submitUecRegistration({
        leadData,
        ...(referralCode ? { referralCode } : {}),
      });
      const provisionData = provisionResult.data as {
        accountActivationRequired?: boolean;
        submissionId?: string;
        isFirstRegistration?: boolean;
        emailAction?: "password-reset" | "email-link-verification";
      };
      setAccountActivationRequired(
        provisionData.accountActivationRequired === true,
      );
      const normalizedEmail = leadData.email.trim().toLowerCase();
      const submissionId = provisionData.submissionId || "";
      const isFirstRegistration = provisionData.isFirstRegistration === true;
      const emailType =
        provisionData.emailAction === "password-reset" || isFirstRegistration
          ? "password-reset"
          : "verification";
      setVerificationEmail(normalizedEmail);
      setPendingSubmissionId(submissionId);
      setAccountEmailType(emailType);
      shouldOpenWhatsApp = isFirstRegistration;
      setIsFirstRegistration(shouldOpenWhatsApp);
      try {
        const delivery = await sendAccountAccessEmail(
          normalizedEmail,
          submissionId,
          emailType,
        );
        setAccountEmailType(delivery.emailType);
        setAccountEmailSent(true);
      } catch (emailError) {
        setAccountEmailSent(false);
        console.error("Failed to send account access email:", emailError);
      }

      // Track form submit
      if (typeof window !== "undefined" && (window as any).gtag) {
        (window as any).gtag("event", "form_submit", {
          event_category: "UEC2026",
          event_label: "registration",
        });
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Failed to save UEC2026 lead:", err);
      setErrors((e) => ({
        ...e,
        submit: "提交失败，请稍后再试。",
      }));
      return;
    } finally {
      submissionLockRef.current = false;
      setIsSubmitting(false);
    }

    if (shouldOpenWhatsApp) {
      setTimeout(() => window.open(WA_COMMUNITY_LINK, "_blank"), 800);
    }
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3 rounded-xl border text-[14px] text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
      errors[field]
        ? "border-red-400 focus:ring-red-200 bg-red-50"
        : "border-slate-200 bg-white focus:border-amber-400 focus:ring-amber-100"
    }`;

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center text-center py-16 px-6"
      >
        <div
          className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
            accountEmailSent
              ? "bg-green-100 text-green-600"
              : "bg-amber-100 text-amber-700"
          }`}
        >
          {accountEmailSent ? (
            <CheckCircle2 className="w-8 h-8" />
          ) : (
            <AlertCircle className="w-8 h-8" />
          )}
        </div>
        <h3
          className="text-[22px] text-slate-900 mb-2"
          style={{ fontFamily: FONT, fontWeight: 800 }}
        >
          {accountEmailSent
            ? "报名成功！🎉"
            : accountEmailType === "password-reset"
              ? "注册信息已保存，但密码设置邮件未发送。"
              : "注册信息已保存，但验证邮件未发送。"}
        </h3>
        <p
          className="text-[14px] text-slate-500 mb-2 leading-relaxed"
          style={{ fontFamily: FONT }}
        >
          {!accountEmailSent
            ? accountEmailType === "password-reset"
              ? "您的注册和账户已创建成功，但您仍需通过密码重置邮件来设置您的密码。"
              : "您的信息已安全保存，但仍需完成验证，验证完成后才会更新到您的账户。"
            : isFirstRegistration
              ? "感谢您的报名！WhatsApp 社群页面即将自动打开。"
              : "感谢您的更新。请验证您的电子邮件以将最新信息更新到您的帐户。"}
        </p>
        <p
          className="text-[13px] text-slate-600 mb-4 leading-relaxed"
          style={{ fontFamily: FONT }}
        >
          {!accountEmailSent
            ? `无法将${
                accountEmailType === "password-reset"
                  ? "密码设置邮件"
                  : "验证链接"
              }发送至 ${verificationEmail}。请点击下方按钮重试，无需重新提交表单。`
            : accountEmailType === "verification" && accountActivationRequired
              ? "请查看您的电子邮件，点击安全登录链接，以验证您的身份并应用最新更改。"
              : "我们已向您的电子邮箱发送密码重置邮件。请点击邮件中的链接设置学生门户账户密码。"}
        </p>
        {!accountEmailSent && (
          <button
            type="button"
            onClick={handleResendAccountEmail}
            disabled={isResendingEmail || resendCooldown > 0}
            className="mb-3 flex items-center gap-2 rounded-xl bg-amber-500 px-6 py-3 text-[14px] font-bold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
            style={{ fontFamily: FONT }}
          >
            {isResendingEmail ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {isResendingEmail
              ? "发送中…"
              : resendCooldown > 0
                ? `请 ${resendCooldown}s 后重试`
                : accountEmailType === "password-reset"
                  ? "重新发送密码设置邮件"
                  : "重新发送验证邮件"}
          </button>
        )}
        {resendMessage && (
          <p
            className={`mb-4 text-[13px] leading-relaxed ${
              accountEmailSent ? "text-green-700" : "text-red-700"
            }`}
            role="status"
          >
            {resendMessage}
          </p>
        )}
        <p
          className="text-[14px] text-slate-600 mb-6"
          style={{ fontFamily: FONT }}
        >
          请点击 <strong>「加入群组」</strong> 加入备考群，
          <br />
          以获取 Zoom 链接、学习资料及最新通知。
        </p>
        <a
          href={WA_COMMUNITY_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-green-500 text-white text-[14px] hover:bg-green-600 transition-colors"
          style={{ fontFamily: FONT, fontWeight: 700 }}
        >
          <MessageCircle className="w-4 h-4" /> 加入 WhatsApp 备考群
        </a>
      </motion.div>
    );
  }

  return (
    <form
      id={id}
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 p-6 lg:p-8"
      style={{ fontFamily: FONT }}
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            className="text-[12px] text-slate-600 uppercase tracking-widest"
            style={{ fontWeight: 700 }}
          >
            中文姓名（选填）
          </label>
          <input
            value={form.chineseName}
            onChange={(e) => set("chineseName", e.target.value)}
            placeholder="例：张文杰"
            className={inputClass("chineseName")}
            style={{ fontFamily: FONT }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            className="text-[12px] text-slate-600 uppercase tracking-widest"
            style={{ fontWeight: 700 }}
          >
            英文姓名 <span className="text-red-500">*</span>
          </label>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="例：Zhang Wenjie"
            className={inputClass("name")}
            style={{ fontFamily: FONT }}
          />
          {errors.name && (
            <p className="text-[11px] text-red-500">{errors.name}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          className="text-[12px] text-slate-600 uppercase tracking-widest"
          style={{ fontWeight: 700 }}
        >
          学校 <span className="text-red-500">*</span>
        </label>
        <input
          value={form.schoolName}
          onChange={(e) => set("schoolName", e.target.value)}
          placeholder="例：育才独立中学"
          className={inputClass("schoolName")}
          style={{ fontFamily: FONT }}
        />
        {errors.schoolName && (
          <p className="text-[11px] text-red-500">{errors.schoolName}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          className="text-[12px] text-slate-600 uppercase tracking-widest"
          style={{ fontWeight: 700 }}
        >
          身份 <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {IDENTITY.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => set("identity", opt.value)}
              className={`px-4 py-2 rounded-xl border text-[13px] transition-all cursor-pointer ${
                form.identity === opt.value
                  ? "bg-amber-500 border-amber-500 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
              }`}
              style={{
                fontFamily: FONT,
                fontWeight: form.identity === opt.value ? 700 : 500,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {errors.identity && (
          <p className="text-[11px] text-red-500">{errors.identity}</p>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            className="text-[12px] text-slate-600 uppercase tracking-widest"
            style={{ fontWeight: 700 }}
          >
            联络号码 <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-row gap-1">
            <span
              className={`w-20 px-3 py-3 rounded-xl border text-[14px] text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                errors["phoneNumber"]
                  ? "border-red-400 focus:ring-red-200 bg-red-50"
                  : "border-slate-200 bg-white focus:border-amber-400 focus:ring-amber-100"
              }`}
            >
              +60
            </span>
            <input
              value={form.phoneNumber}
              onChange={(e) => set("phoneNumber", e.target.value)}
              placeholder="123456789"
              type="tel"
              className={inputClass("phoneNumber")}
              style={{ fontFamily: FONT }}
            />
          </div>
          {errors.phoneNumber && (
            <p className="text-[11px] text-red-500">{errors.phoneNumber}</p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            className="text-[12px] text-slate-600 uppercase tracking-widest"
            style={{ fontWeight: 700 }}
          >
            电邮 <span className="text-red-500">*</span>
          </label>
          <input
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="example@email.com"
            type="email"
            className={inputClass("email")}
            style={{ fontFamily: FONT }}
          />
          {errors.email && (
            <p className="text-[11px] text-red-500">{errors.email}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          className="text-[12px] text-slate-600 uppercase tracking-widest"
          style={{ fontWeight: 700 }}
        >
          您从哪里知道我们？（选填）
        </label>
        <div className="flex flex-wrap gap-2">
          {DISCOVERY_SOURCE.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => set("discoverySource", opt.value)}
              className={`px-3.5 py-1.5 rounded-full border text-[12px] transition-all cursor-pointer ${
                form.discoverySource === opt.value
                  ? "bg-slate-800 border-slate-800 text-white"
                  : "bg-white border-slate-200 text-slate-500 hover:border-slate-400"
              }`}
              style={{
                fontFamily: FONT,
                fontWeight: form.discoverySource === opt.value ? 700 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label
          className="text-[12px] text-slate-600 uppercase tracking-widest"
          style={{ fontWeight: 700 }}
        >
          选择课程 <span className="text-red-500">*</span>
          <span
            className="ml-2 text-[11px] text-slate-400 normal-case"
            style={{ fontWeight: 400 }}
          >
            （可多选）
          </span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {COURSES.map((course) => {
            const selected = courses.includes(course.value);
            return (
              <button
                key={course.value}
                type="button"
                onClick={() => toggleCourse(course.value)}
                className={`px-3 py-2.5 rounded-xl border-2 text-[13px] text-left transition-all flex items-center gap-2 cursor-pointer ${
                  selected
                    ? "border-amber-500 bg-amber-50 text-amber-800"
                    : "border-slate-200 bg-white text-slate-600 hover:border-amber-300"
                }`}
                style={{ fontFamily: FONT, fontWeight: selected ? 700 : 500 }}
              >
                {selected && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                )}
                {!selected && (
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                )}
                <span>{course.label}</span>
              </button>
            );
          })}
        </div>
        {errors.courses && (
          <p className="text-[11px] text-red-500">{errors.courses}</p>
        )}
      </div>

      <div
        className="text-[11px] text-slate-400 leading-relaxed"
        style={{ fontFamily: FONT }}
      >
        提交表格即表示您同意 SCS Education
        收集和使用您的个人资料，以便联系您并提供相关教育资讯。您的资料将被保密处理。
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[16px] shadow-xl shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-xl"
        style={{ fontFamily: FONT, fontWeight: 800 }}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" /> 提交中...
          </>
        ) : (
          <>
            🎓 立即免费报名 <ArrowRight className="w-5 h-5" />
          </>
        )}
      </button>

      {errors.submit && (
        <p className="text-center text-[12px] text-red-500">{errors.submit}</p>
      )}

      <p
        className="text-center text-[12px] text-slate-400"
        style={{ fontFamily: FONT }}
      >
        名额有限，先到先得！提交后将引导您前往 WhatsApp 加入备考群。
      </p>
    </form>
  );
}
